import { ApolloError, gql, useMutation, useQuery } from '@apollo/client';
import IconBook from '@apollo/icons/default/IconBook.svg';
import {
  Card,
  CardBody,
  CardHeader,
  CardHeaderControl,
  CardTitle,
  Divider,
  FormControl,
  Heading,
  IconButton,
  Switch,
  Text,
  Tooltip,
} from '@apollo/orbit';
import classnames from 'classnames';
import { useFormik } from 'formik';
import React, { useCallback, useState } from 'react';

import { LazyUrlModal } from 'src/components/common/LazyUrlModal';
import { Loading } from 'src/components/common/loading/Loading';
import { NotFound } from 'src/components/common/notFound/NotFound';
import { EnterpriseRestrictedFeature } from 'src/components/enterpriseRestrictedFeature/EnterpriseRestrictedFeature';
import { ErrorMessage } from 'src/components/errorMessage/ErrorMessage';
import { useCapabilities } from 'src/hooks/useCapabilities';
import { ignorePermissionsErrors } from 'src/lib/apollo/catchErrors';
import { appLinkContext } from 'src/lib/apollo/link';
import { assertUnreachable } from 'src/lib/assertUnreachable';
import { Config } from 'src/lib/config/config';
import { GraphQLTypes } from 'src/lib/graphqlTypes';
import { DocsRouter } from 'src/lib/routers';
import { Yup } from 'src/lib/yup';

import { GraphRef } from '../../hooks/useGraphRef';
import { UseGraphSettingsButton } from '../variantChecksConfigPage/common/UseGraphSettingsButton';

import { CustomChecksRegisteredWebhookModal } from './CustomChecksRegisteredWebhookModal';
import { RegisteredWebhookSection } from './RegisteredWebhookSection';

/**
 * Schema for the form
 */
const validationSchema = Yup.object().shape({
  useGraphSettings: Yup.boolean().required(),
  enableCustomChecks: Yup.boolean().required(),
});

/**
 * Types inferred from `validationSchema`
 */
type FormValues = Yup.InferType<typeof validationSchema>;

const variantCustomCheckConfigChangeMutation = gql`
  mutation VariantCustomCheckConfigChangeMutation(
    $graphId: ID!
    $graphVariant: String!
    $useGraphSettings: Boolean!
    $enableCustomChecks: Boolean!
  ) {
    service(id: $graphId) {
      variant(name: $graphVariant) {
        updateCheckConfigurationCustomChecks(
          useGraphSettings: $useGraphSettings
          enableCustomChecks: $enableCustomChecks
        ) {
          id
          updatedAt
          updatedBy {
            asActor {
              actorId
              type
            }
            id
            name
          }
          customChecksConfig {
            enableCustomChecks
            useGraphSettings
          }
        }
      }
    }
  }
`;

const graphCustomCheckConfigChangeMutation = gql`
  mutation GraphCustomCheckConfigChangeMutation(
    $graphId: ID!
    $enableCustomChecks: Boolean!
  ) {
    service(id: $graphId) {
      updateCheckConfiguration(enableCustomChecks: $enableCustomChecks) {
        id
        enableCustomChecks
        updatedAt
        updatedBy {
          asActor {
            actorId
            type
          }
          id
          name
        }
      }
    }
  }
`;

export const CustomChecksConfigPage = ({
  graphRef,
  type,
}: {
  graphRef: GraphRef;
  type: 'graph' | 'variant';
}) => {
  const isWebhookCapable = useCapabilities().capabilities?.webhooks ?? false;

  const { data, loading } = useQuery<
    GraphQLTypes.CustomChecksConfigPageQuery,
    GraphQLTypes.CustomChecksConfigPageQueryVariables
  >(
    gql`
      query CustomChecksConfigPageQuery($graphId: ID!, $graphVariant: String!) {
        service(id: $graphId) {
          id
          account {
            id
            name
          }
          checkConfiguration {
            id
            enableCustomChecks
          }
          customCheckConfiguration {
            id
            channel {
              id
              url
              secretTokenIsSet
            }
          }
          variant(name: $graphVariant) {
            id
            customCheckConfiguration {
              id
              channel {
                id
                url
                secretTokenIsSet
              }
            }
            checkConfiguration {
              id
              customChecksConfig {
                useGraphSettings
                enableCustomChecks
              }
            }
          }
        }
      }
    `,
    {
      variables: {
        graphId: graphRef.graphId,
        graphVariant: graphRef.graphVariant,
      },
      context: appLinkContext({ catchErrors: [ignorePermissionsErrors] }),
    },
  );

  const graph = data?.service;
  const useGraphSettings =
    graph?.variant?.checkConfiguration.customChecksConfig.useGraphSettings ??
    true;
  const variantEnableCustomChecks =
    graph?.variant?.checkConfiguration.customChecksConfig.enableCustomChecks ??
    true;
  const graphEnableCustomChecks =
    graph?.checkConfiguration?.enableCustomChecks ?? true;

  const [updateConfigError, setUpdateConfigError] =
    useState<ApolloError | null>(null);

  const [updateCustomChecksVariantCheckConfig] = useMutation<
    GraphQLTypes.VariantCustomCheckConfigChangeMutation,
    GraphQLTypes.VariantCustomCheckConfigChangeMutationVariables
  >(variantCustomCheckConfigChangeMutation, {
    onError: setUpdateConfigError,
  });

  const [updateCustomChecksGraphCheckConfig] = useMutation<
    GraphQLTypes.GraphCustomCheckConfigChangeMutation,
    GraphQLTypes.GraphCustomCheckConfigChangeMutationVariables
  >(graphCustomCheckConfigChangeMutation, {
    onError: setUpdateConfigError,
  });

  const { values, isSubmitting, setFieldValue, handleChange, submitForm } =
    useFormik<FormValues>({
      enableReinitialize: true,
      initialValues: {
        useGraphSettings,
        enableCustomChecks:
          type === 'variant'
            ? variantEnableCustomChecks
            : graphEnableCustomChecks,
      },
      onSubmit: async (formValues) => {
        if (type === 'variant') {
          await updateCustomChecksVariantCheckConfig({
            variables: {
              graphId: graphRef.graphId,
              graphVariant: graphRef.graphVariant,
              useGraphSettings: formValues.useGraphSettings,
              enableCustomChecks: formValues.enableCustomChecks,
            },
          });
        } else {
          await updateCustomChecksGraphCheckConfig({
            variables: {
              graphId: graphRef.graphId,
              enableCustomChecks: formValues.enableCustomChecks,
            },
          });
        }
      },
    });

  const clearErrorsAndSubmit = useCallback(
    (e?: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
      setUpdateConfigError(null);
      if (e) {
        handleChange(e);
      }
      submitForm();
    },
    [setUpdateConfigError, handleChange, submitForm],
  );

  if (loading) {
    return <Loading />;
  } else if (!isWebhookCapable) {
    return (
      <EnterpriseRestrictedFeature
        analyticsCategory="Webhooks"
        analyticsLabel="Settings: Custom Checks: non-enterprise"
      />
    );
  } else if (!graph) {
    return <NotFound />;
  }

  const disableCardContent =
    (type === 'variant' && values.useGraphSettings) || isSubmitting;

  const customCheckConfiguration =
    type === 'variant'
      ? graph.variant?.customCheckConfiguration
      : type === 'graph'
        ? graph.customCheckConfiguration
        : assertUnreachable(type);

  return (
    <Card
      {...(type === 'variant'
        ? {
            actions: (
              <UseGraphSettingsButton
                label="Use Graph Settings"
                tooltipContent="Default to this graph's proposals check configuration."
                isSelected={values.useGraphSettings}
                onChange={(value) => {
                  setFieldValue('useGraphSettings', value);
                  clearErrorsAndSubmit();
                }}
              />
            ),
          }
        : {})}
    >
      <CardHeader>
        <CardTitle>
          General custom check configuration{' '}
          <Tooltip label="View documentation" placement="top">
            <IconButton
              aria-label="View documentation"
              variant="secondary"
              as="a"
              target="_blank"
              rel="noopener noreferrer"
              size="sm"
              href={DocsRouter.path('CustomChecks')}
              icon={<IconBook />}
            />
          </Tooltip>
        </CardTitle>

        {type === 'variant' && (
          <CardHeaderControl>
            <UseGraphSettingsButton
              label="Use Graph Settings"
              tooltipContent="Default to this graph's proposals check configuration."
              isSelected={values.useGraphSettings}
              onChange={(value) => {
                setFieldValue('useGraphSettings', value);
                clearErrorsAndSubmit();
              }}
            />
          </CardHeaderControl>
        )}
      </CardHeader>
      <CardBody>
        {updateConfigError && (
          <ErrorMessage className="my-6" error={updateConfigError} />
        )}

        <div className="flex justify-between">
          <div className="flex-3 flex-col">
            <Heading className="flex flex-row items-center gap-2" as="h3">
              Enable custom checks{' '}
              <span>
                <FormControl>
                  <Switch
                    data-analytics-label={`custom_checks ${
                      values.enableCustomChecks ? 'on' : 'off'
                    }`}
                    isChecked={values.enableCustomChecks}
                    isDisabled={disableCardContent}
                    className={classnames(
                      'shrink-0 items-start',
                      disableCardContent
                        ? 'opacity-100 [&>div:nth-child(3)]:opacity-50 [&>div:nth-child(4)]:opacity-50'
                        : 'cursor-pointer',
                    )}
                    onChange={(e) => {
                      setFieldValue('enableCustomChecks', e.target.checked);
                      clearErrorsAndSubmit();
                    }}
                  />
                </FormControl>
              </span>
            </Heading>
            <Text>
              Run tailored checks on your{' '}
              {type === 'variant' ? 'variant' : 'graph'} for better control.
            </Text>
          </div>
          <div className="flex-1" />
        </div>
        <Divider />
        <RegisteredWebhookSection
          disableCardContent={disableCardContent}
          endpointUrl={customCheckConfiguration?.channel.url}
          secretTokenIsSet={
            !!customCheckConfiguration?.channel.secretTokenIsSet
          }
          variant={graphRef.graphVariant}
          graphId={graphRef.graphId}
          customChecksEnabled={values.enableCustomChecks}
        />
        <LazyUrlModal name={Config.modals.editCustomChecksWebhook}>
          {({ closeModal, isOpen }) => (
            <CustomChecksRegisteredWebhookModal
              currentEndpointUrl={customCheckConfiguration?.channel.url ?? ''}
              secretTokenIsSet={
                !!customCheckConfiguration?.channel.secretTokenIsSet
              }
              graphRef={graphRef}
              type={type}
              isOpen={isOpen}
              onClose={closeModal}
            />
          )}
        </LazyUrlModal>
      </CardBody>
    </Card>
  );
};
